import { isMapId } from "@69ly/maps";
import type { FaceitListMatch, FaceitOpponent, FaceitTournament } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Avatar } from "../components/Avatar.tsx";
import { Icon } from "../components/Icon.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { ApiFailure } from "../lib/api.ts";
import { errorText } from "../lib/errors.ts";
import { useFaceitMatches, useTeamFaceit } from "../lib/faceit-queries.ts";
import { mapIconUrl } from "../lib/map-art.ts";
import { LabelBadge, mapName } from "./Matches.tsx";

/**
 * Matches › FACEIT: the members' recent FACEIT matches and the team's championships,
 * with each opponent's matches to scout. Those already in the library are marked;
 * importing from here waits for FACEIT's Downloads API.
 */

const time = (m: FaceitListMatch) => m.startedAt ?? m.scheduledAt ?? m.finishedAt;

function useWhen() {
  const { i18n } = useTranslation();
  return (iso: string | null, withTime = false) =>
    iso ? new Date(iso).toLocaleString(i18n.language, withTime ? { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" } : { day: "numeric", month: "short" }) : "–";
}

/** A match's day, and its time when it is still to play: two short lines. */
function When({ iso, withTime }: { iso: string | null; withTime: boolean }) {
  const { i18n } = useTranslation();
  if (!iso) return <span className="faceit-row-when mono small">–</span>;
  const d = new Date(iso);
  return (
    <span className="faceit-row-when mono small">
      <span>{d.toLocaleDateString(i18n.language, withTime ? { weekday: "short", day: "numeric", month: "short" } : { day: "numeric", month: "short" })}</span>
      {withTime && <span>{d.toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" })}</span>}
    </span>
  );
}

/** The score from one side: rounds for a BO1 when FACEIT gives them, maps otherwise. */
function scoreOf(m: FaceitListMatch, side: 0 | 1): [number, number] | null {
  if (m.status !== "finished") return null;
  const flip = (s: [number, number]): [number, number] => (side === 0 ? s : [s[1], s[0]]);
  if (m.bestOf === 1 && m.mapScores[0]) return flip(m.mapScores[0]);
  const [a, b] = [m.factions[0].score, m.factions[1].score];
  return a === null || b === null ? null : flip([a, b]);
}

/** One FACEIT match: when, who against whom (one side first), the score, the maps; open the room, or the library's match. */
function MatchRow({ m, side, showLabel = false, neutral = false, downloads }: { m: FaceitListMatch; side: 0 | 1; showLabel?: boolean; neutral?: boolean; downloads: boolean }) {
  const { t } = useTranslation();
  const [a, b] = [m.factions[side], m.factions[1 - side]];
  const score = scoreOf(m, side);
  // Another team's result is not ours: no green or red for it.
  const result = neutral || m.status !== "finished" || m.winner === null ? null : m.winner === side ? "win" : "loss";
  const upcoming = m.status === "upcoming" || m.status === "ongoing";
  return (
    <li className={`faceit-row${upcoming ? " is-upcoming" : ""}${m.status === "cancelled" ? " is-cancelled" : ""}`}>
      <When iso={time(m)} withTime={upcoming} />
      <div className="faceit-row-main">
        <span className="faceit-row-sides">
          <b>{a.name}</b>
          <span className="muted">{t("faceit.vs")}</span>
          <span>{b.name}</span>
        </span>
        <span className="muted small faceit-row-meta">
          {m.competition.name}
          {m.stage && ` · ${m.stage}`}
          {m.bestOf > 1 && ` · BO${m.bestOf}`}
          {m.status === "ongoing" && <span className="faceit-live"> · {t("faceit.live")}</span>}
          {m.status === "cancelled" && ` · ${t("faceit.cancelled")}`}
        </span>
      </div>
      {m.maps.length > 0 && (
        <span className="faceit-row-maps" aria-label={m.maps.map(mapName).join(", ")}>
          {m.maps.slice(0, 5).map((x, i) => (isMapId(x) ? <img key={i} src={mapIconUrl(x)} alt="" width={20} height={20} title={mapName(x)} /> : null))}
        </span>
      )}
      {showLabel && m.label !== "scouting" && <LabelBadge label={m.label} />}
      <span className={`faceit-row-score mono${result ? ` is-${result}` : ""}`}>
        {score ? `${score[0]}–${score[1]}` : upcoming ? t("faceit.upcoming") : "–"}
        {result && <span className="visually-hidden"> {t(`faceit.${result}`)}</span>}
      </span>
      <span className="faceit-row-actions">
        {m.libraryMatchId ? (
          <Link to={`/matches/${m.libraryMatchId}`} className="btn btn-secondary btn-small">
            <Icon name="check" size={14} />
            {t("faceit.inLibrary")}
          </Link>
        ) : m.status === "finished" ? (
          <button type="button" className="btn btn-quiet btn-small" disabled={!downloads} title={downloads ? undefined : t("faceit.importWaits")}>
            <Icon name="download" size={14} />
            {t("faceit.import")}
          </button>
        ) : null}
        <a className="btn btn-quiet btn-small" href={m.url} target="_blank" rel="noreferrer" aria-label={t("faceit.roomOf", { a: a.name, b: b.name })}>
          {t("faceit.room")}
          <Icon name="external" size={12} />
        </a>
      </span>
    </li>
  );
}

function Opponent({ o, downloads }: { o: FaceitOpponent; downloads: boolean }) {
  const { t } = useTranslation();
  const when = useWhen();
  const next = o.versus.find((m) => m.status === "upcoming" || m.status === "ongoing") ?? null;
  const played = o.versus.filter((m) => m.status === "finished");
  const imported = o.matches.filter((m) => m.libraryMatchId).length;
  // Their side in a match of theirs, for reading it from them.
  const theirs = (m: FaceitListMatch): 0 | 1 => (m.factions[1].id === o.team.id ? 1 : 0);
  return (
    <details className="faceit-opponent">
      <summary>
        <Avatar name={o.team.name} url={o.team.avatar} size={24} />
        <b className="faceit-opponent-name">{o.team.name}</b>
        {next && <span className="chip chip-small faceit-next-chip">{t("faceit.nextVs", { when: when(time(next), true) })}</span>}
        {played.map((m) => {
          const s = scoreOf(m, m.ours ?? 0);
          return (
            <span key={m.id} className={`chip chip-small ${m.winner === m.ours ? "is-win" : "is-loss"}`}>
              {m.winner === m.ours ? t("faceit.wonShort") : t("faceit.lostShort")} {s ? `${s[0]}–${s[1]}` : ""}
            </span>
          );
        })}
        <span className="muted small faceit-opponent-count">
          {t("faceit.theirMatches", { count: o.matches.length })}
          {imported > 0 && ` · ${t("faceit.imported", { count: imported })}`}
        </span>
      </summary>
      {o.matches.length === 0 ? (
        <p className="muted small">{t("faceit.noOtherMatches")}</p>
      ) : (
        <ul className="faceit-rows">
          {o.matches.map((m) => (
            <MatchRow key={m.id} m={m} side={theirs(m)} neutral downloads={downloads} />
          ))}
        </ul>
      )}
      <a className="small" href={o.team.url} target="_blank" rel="noreferrer">
        {t("faceit.teamPage", { name: o.team.name })} <Icon name="external" size={12} />
      </a>
    </details>
  );
}

const OPPONENTS_SHOWN = 8;

function Tournament({ tr, downloads }: { tr: FaceitTournament; downloads: boolean }) {
  const { t } = useTranslation();
  const when = useWhen();
  const [allOpponents, setAllOpponents] = useState(false);
  const opponents = allOpponents ? tr.opponents : tr.opponents.slice(0, OPPONENTS_SHOWN);
  const nextSide = tr.next?.ours ?? 0;
  return (
    <section className="card faceit-tournament" aria-labelledby={`ft-${tr.id}`}>
      <header className="faceit-tournament-head">
        <div>
          <h3 id={`ft-${tr.id}`} className="section-title faceit-tournament-name">
            <a href={tr.url} target="_blank" rel="noreferrer">
              {tr.name} <Icon name="external" size={14} />
            </a>
          </h3>
          <span className="muted small">
            {t(`faceit.status.${tr.status}`)} · {t("faceit.record", { won: tr.record[0], lost: tr.record[1] })}
          </span>
        </div>
      </header>
      {tr.next && (
        <div className="faceit-next">
          <span className="eyebrow">{t("faceit.next")}</span>
          <b className="faceit-next-vs">{t("faceit.versus", { name: tr.next.factions[1 - nextSide].name })}</b>
          <span className="muted">
            {when(time(tr.next), true)}
            {tr.next.stage && ` · ${tr.next.stage}`}
          </span>
        </div>
      )}
      {tr.matches.length > 0 && (
        <>
          <h4 className="eyebrow">{t("faceit.ourMatches")}</h4>
          <ul className="faceit-rows">
            {tr.matches.map((m) => (
              <MatchRow key={m.id} m={m} side={m.ours ?? 0} downloads={downloads} />
            ))}
          </ul>
        </>
      )}
      {tr.opponents.length > 0 && (
        <>
          <h4 className="eyebrow">{t("faceit.opponents", { count: tr.opponents.length })}</h4>
          <p className="muted small">{t("faceit.opponentsHelp")}</p>
          <div className="faceit-opponents">
            {opponents.map((o) => (
              <Opponent key={o.team.id} o={o} downloads={downloads} />
            ))}
          </div>
          {tr.opponents.length > OPPONENTS_SHOWN && (
            <button type="button" className="btn btn-quiet btn-small" onClick={() => setAllOpponents((v) => !v)}>
              {allOpponents ? t("faceit.fewerTeams") : t("faceit.allTeams", { count: tr.opponents.length })}
            </button>
          )}
        </>
      )}
    </section>
  );
}

const RECENT_FILTERS = ["all", "team", "stack", "solo"] as const;

export function FaceitMatchesView({ teamId }: { teamId: string }) {
  const { t } = useTranslation();
  const team = useTeamFaceit(teamId);
  const configured = team.data?.configured ?? false;
  const list = useFaceitMatches(teamId, configured);
  const [filter, setFilter] = useState<(typeof RECENT_FILTERS)[number]>("all");

  if (team.isPending) return <Loading />;
  if (team.error) return <ErrorState message={errorText(t, team.error)} onRetry={() => void team.refetch()} />;
  if (!configured)
    return (
      <div className="card learn-empty">
        <h2 className="section-title">{t("faceit.offTitle")}</h2>
        <p className="muted">{t("faceit.off")}</p>
      </div>
    );

  const data = list.data;
  const downloads = data?.downloads ?? false;
  const recent = (data?.recent ?? []).filter((m) => filter === "all" || m.label === filter);
  return (
    <div className="faceit-view">
      {!downloads && (
        <div className="faceit-howto">
          <Icon name="download" />
          <div>
            <b>{t("faceit.howtoTitle")}</b>
            <p className="muted small">{t("faceit.howto")}</p>
          </div>
        </div>
      )}
      {!team.data?.team && (
        <p className="muted small">
          {team.data?.canEdit ? t("faceit.linkFirst") : t("faceit.linkFirstMember")} <Link to="/settings/team">{t("faceit.toTeam")}</Link>
        </p>
      )}
      {list.isPending ? (
        <Loading />
      ) : list.error ? (
        <ErrorState message={list.error instanceof ApiFailure && list.error.code === "faceit_failed" ? list.error.message : errorText(t, list.error)} onRetry={() => void list.refetch()} />
      ) : (
        <>
          {data!.missing.length > 0 && <p className="muted small">{t("faceit.missing", { names: data!.missing.join(", "), count: data!.missing.length })}</p>}
          {data!.tournaments.length > 0 && (
            <section aria-labelledby="faceit-tournaments" className="faceit-section">
              <h2 id="faceit-tournaments" className="eyebrow">
                {t("faceit.tournaments")}
              </h2>
              {data!.tournaments.map((tr) => (
                <Tournament key={tr.id} tr={tr} downloads={downloads} />
              ))}
            </section>
          )}
          <section aria-labelledby="faceit-recent" className="faceit-section">
            <div className="faceit-section-head">
              <h2 id="faceit-recent" className="eyebrow">
                {t("faceit.recent")}
              </h2>
              <div className="segmented segmented-small" role="group" aria-label={t("matches.whoPlayed")}>
                {RECENT_FILTERS.map((f) => (
                  <button key={f} type="button" className="seg" aria-pressed={filter === f} onClick={() => setFilter(f)}>
                    {f === "all" ? t("matches.all") : t(`matches.label.${f}`)}
                  </button>
                ))}
              </div>
            </div>
            {recent.length === 0 ? (
              <p className="muted">{t("faceit.noRecent")}</p>
            ) : (
              <ul className="card faceit-rows">
                {recent.map((m) => (
                  <MatchRow key={m.id} m={m} side={m.ours ?? 0} showLabel downloads={downloads} />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
}
